import { connectNetsByDescriptor, disconnectPinsByDescriptor, getTerminalFromPinDescriptor, handleUpdateNetsLabel, } from "../document-actions";
// Regex for Component:Pin format (e.g., "U1:P1", "U1:~CHARG", "J1:D-", "U2:I/O1")
// RefDes: strict alphanumeric + underscore
// PinName: permissive — accepts any non-empty string after the colon, because
// users and library parts may define pins with any characters (~ # / + - etc.)
export const PIN_REGEX = /^([a-zA-Z0-9_]+):(.+)$/;
// Regex for Net format (e.g., "^GND")
export const NET_REGEX = /^\^(.+)$/;
function parsePin(token) {
    const match = token.match(PIN_REGEX);
    if (!match)
        return null;
    return { component: match[1] ?? "", pinDesignator: match[2] ?? "" };
}
function parseNet(token) {
    const match = token.match(NET_REGEX);
    return match ? match[1] ?? null : null;
}
// ============================================================================
// Net Naming Helpers
// ============================================================================
/**
 * Collect all existing net labels from the document.
 */
export function getExistingNetLabels(document) {
    return new Set(Object.values(document.nets).map((net) => net.label));
}
/**
 * Generate a unique net name by appending an incrementing suffix if needed.
 * If baseName is "TIMER" and "TIMER" exists, returns "TIMER 2", "TIMER 3", etc.
 * If baseName is "Net" and "Net 1" exists, returns "Net 2", etc.
 */
export function generateUniqueNetName(baseName, existing) {
    if (!existing.has(baseName))
        return baseName;
    let counter = 2;
    while (existing.has(`${baseName} ${counter}`)) {
        counter++;
    }
    return `${baseName} ${counter}`;
}
/**
 * Generate the next sequential "Net N" name that doesn't exist in the document.
 * Returns "Net 1", "Net 2", etc. — always the lowest unused number.
 */
function generateSequentialNetName(existing) {
    let counter = 1;
    while (existing.has(`Net ${counter}`)) {
        counter++;
    }
    return `Net ${counter}`;
}
/**
 * Validate all operations upfront before any mutations.
 * Throws a descriptive error if any operation is invalid.
 *
 * Tracks "pending" net names — nets that will be created by earlier connect
 * operations in this batch — so that later operations can reference them
 * with ^NetName syntax without requiring a separate tool call.
 */
function validateAndParseOperations(action, document) {
    const errors = [];
    const connects = [];
    const disconnects = [];
    const renames = [];
    // Track existing + pending net labels so later operations can reference
    // nets created by earlier operations in the same batch.
    const existingNetLabels = getExistingNetLabels(document);
    const pendingNetLabels = new Set();
    // Track which netNameSuggestion values we've seen, so duplicate suggestions
    // merge (same suggestion in same batch = same target net).
    const pendingSuggestions = new Set();
    // Partition and parse operations by type
    for (const op of action.operations) {
        if (op.action === "connect") {
            const pinDescriptors = [];
            const netNames = [];
            const unparseable = [];
            for (const t of op.targets) {
                const p = parsePin(t);
                if (p) {
                    pinDescriptors.push(p);
                    continue;
                }
                const n = parseNet(t);
                if (n) {
                    netNames.push(n);
                    continue;
                }
                unparseable.push(t);
            }
            // Error: unparseable targets
            if (unparseable.length > 0) {
                errors.push(`Connect: invalid target(s) ${unparseable.map((t) => `"${t}"`).join(", ")}. ` +
                    `Expected "Ref:Pin" (e.g. "U1:P1") or "^NetName" (e.g. "^GND").`);
            }
            // Error: multiple nets
            const uniqueNets = Array.from(new Set(netNames));
            if (uniqueNets.length > 1) {
                errors.push(`Connect: cannot merge multiple nets in one operation: ${uniqueNets
                    .map((n) => `^${n}`)
                    .join(", ")}. ` + `At most one net may be specified per connect.`);
            }
            // Error: ^Net references a net that doesn't exist (in the document or pending from this batch)
            if (uniqueNets.length === 1 && uniqueNets[0]) {
                if (!existingNetLabels.has(uniqueNets[0]) && !pendingNetLabels.has(uniqueNets[0])) {
                    errors.push(`Connect: net "^${uniqueNets[0]}" does not exist in the document. ` +
                        `Use ^NetName only to join an existing net (or one created by an earlier operation in this batch). ` +
                        `To create a new net with a suggested name, use the netNameSuggestion field instead.`);
                }
            }
            // Error: pin targets that don't exist in the document
            for (const pin of pinDescriptors) {
                const terminal = getTerminalFromPinDescriptor(document, pin);
                if (!terminal) {
                    errors.push(`Connect: pin "${pin.component}:${pin.pinDesignator}" does not exist in the document.`);
                }
            }
            const parsedSuggestion = op.netNameSuggestion?.replace(/^\^/, "");
            connects.push({
                pinDescriptors,
                netName: uniqueNets[0],
                netNameSuggestion: parsedSuggestion,
                isExplicitNetName: uniqueNets.length === 1 || (pinDescriptors.length <= 1 && !!parsedSuggestion),
                targets: op.targets,
            });
            // Track the net name this operation will create so subsequent
            // operations can reference it with ^NetName in the same batch.
            // If two ops share the same netNameSuggestion, they'll merge in
            // consolidation — only the first one creates a pending name.
            if (!uniqueNets[0]) {
                if (parsedSuggestion && pendingSuggestions.has(parsedSuggestion)) {
                    // Same suggestion already seen — this op will merge, no new name created
                }
                else {
                    const allKnown = new Set([...existingNetLabels, ...pendingNetLabels]);
                    const resolvedName = parsedSuggestion
                        ? generateUniqueNetName(parsedSuggestion, allKnown)
                        : generateSequentialNetName(allKnown);
                    pendingNetLabels.add(resolvedName);
                    if (parsedSuggestion) {
                        pendingSuggestions.add(parsedSuggestion);
                    }
                }
            }
        }
        else if (op.action === "disconnect") {
            const pinDescriptors = [];
            const unparseable = [];
            for (const pin of op.pins) {
                const p = parsePin(pin);
                if (p) {
                    pinDescriptors.push(p);
                }
                else {
                    unparseable.push(pin);
                }
            }
            if (unparseable.length > 0) {
                errors.push(`Disconnect: invalid pin(s) ${unparseable.map((t) => `"${t}"`).join(", ")}. ` +
                    `Expected "Ref:Pin" (e.g. "U1:P1").`);
            }
            // Error: pin targets that don't exist in the document
            for (const pin of pinDescriptors) {
                const terminal = getTerminalFromPinDescriptor(document, pin);
                if (!terminal) {
                    errors.push(`Disconnect: pin "${pin.component}:${pin.pinDesignator}" does not exist in the document.`);
                }
            }
            disconnects.push({ pinDescriptors, rawPins: op.pins });
        }
        else if (op.action === "rename") {
            const oldName = parseNet(op.currentNet);
            const newName = parseNet(op.newNet);
            if (!oldName) {
                errors.push(`Rename: invalid current net "${op.currentNet}". Expected "^NetName" format.`);
            }
            if (!newName) {
                errors.push(`Rename: invalid new net "${op.newNet}". Expected "^NetName" format.`);
            }
            if (oldName) {
                // Check that the net actually exists in the document
                const matchingNets = Object.values(document.nets).filter((net) => net.label === oldName);
                if (matchingNets.length === 0) {
                    errors.push(`Rename: net "^${oldName}" does not exist in the document.`);
                }
            }
            if (oldName && newName) {
                renames.push({ oldName, newName, rawCurrentNet: op.currentNet, rawNewNet: op.newNet });
            }
        }
        // "view" ops need no validation
    }
    if (errors.length > 0) {
        throw new Error(`Validation failed:\n${errors.map((e) => `• ${e}`).join("\n")}`);
    }
    return { connects, disconnects, renames };
}
// ============================================================================
// Consolidation
// ============================================================================
/**
 * Consolidate connect ops that share the same target net.
 * Merging rules:
 * 1. Multiple ^Net ops targeting the same name → merge into one net.
 * 2. A later ^NetName op referencing a name created by netNameSuggestion → merge.
 * 3. Multiple ops with the same netNameSuggestion → merge (same batch = same net).
 * Non-explicit ops (auto-named) with unique names pass through unchanged.
 */
function consolidateConnectsByNetName(connects, existingNetLabels) {
    const result = [];
    // Track resolved net names → index in result[], covering explicit ^Net
    // ops AND resolved netNameSuggestion ops so later references merge correctly.
    const netNameToIndex = new Map();
    // Track raw netNameSuggestion → index, so duplicate suggestions in the
    // same batch merge into one net instead of auto-incrementing.
    const suggestionToIndex = new Map();
    for (const conn of connects) {
        // If this op uses the same netNameSuggestion as an earlier op,
        // merge directly — no need to generate a unique name.
        if (!conn.netName && conn.netNameSuggestion && suggestionToIndex.has(conn.netNameSuggestion)) {
            const idx = suggestionToIndex.get(conn.netNameSuggestion);
            const existing = result[idx];
            existing.pinDescriptors.push(...conn.pinDescriptors);
            existing.targets.push(...conn.targets.filter((t) => !parseNet(t)));
            continue;
        }
        // Determine the target net name (same logic as the execution loop)
        let targetNet;
        if (conn.netName) {
            targetNet = conn.netName;
        }
        else if (conn.netNameSuggestion) {
            targetNet = generateUniqueNetName(conn.netNameSuggestion, existingNetLabels);
        }
        else {
            targetNet = generateSequentialNetName(existingNetLabels);
        }
        existingNetLabels.add(targetNet);
        // Merge if we've already seen an op targeting this resolved net name
        // (e.g. ^GND in two separate ops, or ^USB_DM referencing a netNameSuggestion op)
        if (netNameToIndex.has(targetNet)) {
            const idx = netNameToIndex.get(targetNet);
            const existing = result[idx];
            existing.pinDescriptors.push(...conn.pinDescriptors);
            existing.targets.push(...conn.targets.filter((t) => !parseNet(t))); // don't duplicate ^Net tokens
        }
        else {
            const idx = result.length;
            result.push({
                ...conn,
                // Override the resolved net name so the execution loop doesn't re-resolve
                netName: conn.netName ?? undefined,
                netNameSuggestion: conn.netName ? undefined : conn.netNameSuggestion,
                // Store the resolved name so the execution loop doesn't re-resolve
                resolvedNetName: targetNet,
            });
            netNameToIndex.set(targetNet, idx);
            if (conn.netNameSuggestion && !conn.netName) {
                suggestionToIndex.set(conn.netNameSuggestion, idx);
            }
        }
    }
    return result;
}
// ============================================================================
// Main Action
// ============================================================================
/**
 * Applies connection operations (connect, disconnect, rename) to the document.
 * Operations are reordered: disconnects first, then connects, then renames.
 * A view is always appended at the end (merged from explicit views, or auto-generated
 * from all component designators mentioned in the operations).
 *
 * All operations are validated upfront before any mutations. If any target is invalid,
 * the entire action fails with a descriptive error.
 *
 * Disconnect removes all listed pins from all of their current nets.
 *
 * This is the applyActions-compatible version of the netlist tool's mutation logic.
 */
export function applyConnectionsAction(action, document, actionContext) {
    // --- Phase 1: Validate all operations upfront ---
    const { connects, disconnects, renames } = validateAndParseOperations(action, document);
    const results = { netsUpdated: [] };
    const existingNetLabels = getExistingNetLabels(document);
    // --- Phase 2: Execute operations ---
    // Order: disconnects → connects → renames.
    // Disconnects run first so that pins are cleanly removed from old nets
    // before being wired into new ones, preventing unintended net merges
    // (e.g. connecting pins already on GND to a new TIMING net would
    // otherwise merge TIMING into GND).
    // 2a. Process disconnects (removes pins from ALL their nets)
    for (const disc of disconnects) {
        disconnectPinsByDescriptor(document, disc.pinDescriptors);
        results.netsUpdated?.push({
            netName: "",
            action: "disconnect",
            pins: disc.pinDescriptors.map((p) => ({
                componentDesignator: p.component,
                pinDesignator: p.pinDesignator,
            })),
        });
    }
    // 2b. Process connects
    // Consolidate ops sharing the same explicit ^Net name to avoid creating
    // duplicate nets (e.g. two separate ops both targeting ^GND).
    const consolidatedConnects = consolidateConnectsByNetName(connects, existingNetLabels);
    for (const conn of consolidatedConnects) {
        // Net name was already resolved during consolidation
        const targetNet = conn.resolvedNetName ??
            conn.netName ??
            (conn.netNameSuggestion
                ? generateUniqueNetName(conn.netNameSuggestion, existingNetLabels)
                : generateSequentialNetName(existingNetLabels));
        connectNetsByDescriptor(document, [{ netName: targetNet, pins: conn.pinDescriptors, isExplicitNetName: conn.isExplicitNetName }], actionContext);
        results.netsUpdated?.push({
            netName: targetNet,
            action: "connect",
            pins: conn.pinDescriptors.map((p) => ({ componentDesignator: p.component, pinDesignator: p.pinDesignator })),
        });
    }
    // 2c. Process renames
    for (const ren of renames) {
        const matchingNetIds = Object.entries(document.nets)
            .filter(([, net]) => net.label === ren.oldName)
            .map(([id]) => id);
        if (matchingNetIds.length === 0)
            continue;
        const netLabelMap = {};
        for (const netId of matchingNetIds) {
            netLabelMap[netId] = ren.newName;
        }
        handleUpdateNetsLabel(document, netLabelMap);
    }
    // --- Phase 3: Build merged view ---
    const views = action.operations.filter((op) => op.action === "view");
    const viewComponents = new Set();
    for (const op of views) {
        if (op.action !== "view")
            continue;
        if (op.components) {
            for (const c of op.components) {
                viewComponents.add(c);
            }
        }
    }
    // If no explicit view, auto-generate from all component designators mentioned in operations
    if (views.length === 0) {
        for (const conn of connects) {
            for (const t of conn.targets) {
                const p = parsePin(t);
                if (p)
                    viewComponents.add(p.component);
            }
        }
        for (const disc of disconnects) {
            for (const pin of disc.rawPins) {
                const p = parsePin(pin);
                if (p)
                    viewComponents.add(p.component);
            }
        }
    }
    // Store the view filter on the result so callers can use it
    if (viewComponents.size > 0) {
        results.netlistViewComponents = Array.from(viewComponents).sort();
    }
    return results;
}
//# sourceMappingURL=applyConnectionsAction.js.map